import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import { Input, Text, Checkbox } from 'app/seedly-component-library';
import Button from 'app/seedly-component-library/button-new';
import DropdownSelect from 'seedly-component-library/dropdown-select';
import { Trash2 as Trash } from 'react-feather';

import {
  CREDENTIAL_TYPE,
  CREDENTIAL_TYPE_LIST,
  YEAR_OPTIONS,
} from 'app/constants/profile';
import {
  postCredential,
  updateCredential,
  deleteCredential,
} from 'app/dispatcher/credentialDispatcher';
import ProfileCredentialIcon from 'app/components/profile/ProfileCredentialIcon';
import { useFormik } from 'formik';
import { validator } from './validation';
import withModal from './withModal';

const defaultCredentialState = {
  type: CREDENTIAL_TYPE.EMPLOYMENT,
  name: '',
  desc: '',
  startYear: null,
  endYear: null,
  isCurrent: false,
  isDefault: false,
};

const makeCredentialsOptions = onChange => {
  return CREDENTIAL_TYPE_LIST.map(type => {
    const capitalizedCrendetialType =
      type.charAt(0).toUpperCase() + type.slice(1);
    return {
      title: capitalizedCrendetialType,
      value: type,
      icon: <ProfileCredentialIcon credentialType={type} />,
      onClick: () => onChange(type),
    };
  });
};

const makeYearOptions = onChange => {
  return YEAR_OPTIONS.map(year => {
    return { title: year, value: year, onClick: () => onChange(year) };
  });
};

const setInitialState = (isEdit, editedCredential) => {
  const { credentialType, isDefault, ...otherDetails } = editedCredential;
  if (isEdit) {
    return {
      type: credentialType,
      isDefault,
      ...otherDetails,
    };
  }
  return defaultCredentialState;
};

const PostCredentialForm = props => {
  const { isEdit, editedCredential, resetForm } = props;
  const initalState = setInitialState(isEdit, editedCredential);

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: initalState,
    onSubmit: (values, formikBag) => {
      if (isEdit) {
        dispatch(
          updateCredential(
            { id: editedCredential.id, credentialDetails: values },
            { onSuccess: resetForm(), onFailure: () => {} },
          ),
        );
      } else {
        dispatch(
          postCredential(
            { credentialDetails: values },
            { onSuccess: resetForm(), onFailure: () => {} },
          ),
        );
      }
      formikBag.resetForm();
    },
    validate: validator,
  });

  const { errors, isSubmitting } = formik;
  const { type, name, desc, isDefault, isCurrent } = formik.values;

  useEffect(() => {
    if (isCurrent) {
      formik.setFieldValue('endYear', new Date().getFullYear());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrent]);

  const credentialOptions = makeCredentialsOptions(t =>
    formik.setFieldValue('type', t),
  );
  const startYearOptions = makeYearOptions(year =>
    formik.setFieldValue('startYear', year),
  );
  const endYearOptions = makeYearOptions(year =>
    formik.setFieldValue('endYear', year),
  );
  const stopSubmit = !isEmpty(errors) || isSubmitting;

  const { startYear, endYear } = formik.values;

  return (
    <div className="flex flex-col gap-3 w-full">
      <DropdownSelect
        variant="outline"
        items={credentialOptions}
        defaultSelectedItem={credentialOptions[0]}
        handleSelectedItemchange={item => {
          item.selectedItem.onClick();
        }}
      />
      <Input
        placeholder={
          type === CREDENTIAL_TYPE.EMPLOYMENT ? 'Company Name' : 'School Name'
        }
        width="100%"
        name="name"
        value={name}
        onChange={formik.handleChange}
        error={!!errors.name}
      />
      {!!errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
      <Input
        placeholder={
          type === CREDENTIAL_TYPE.EMPLOYMENT ? 'Designation' : 'Major'
        }
        name="desc"
        width="100%"
        value={desc}
        onChange={formik.handleChange}
        error={!!errors.desc}
      />
      {!!errors.desc && (
        <Text variant="caption" intent="error">
          {errors.desc}
        </Text>
      )}
      <div className="flex gap-4 items-center">
        <DropdownSelect
          variant="outline"
          items={startYearOptions}
          placeHolder="Start Year"
          defaultSelectedItem={
            startYear && { title: startYear, value: startYear }
          }
          handleSelectedItemchange={item => {
            item.selectedItem.onClick();
          }}
        />
        <DropdownSelect
          variant="outline"
          items={endYearOptions}
          placeHolder="End Year"
          defaultSelectedItem={endYear && { title: endYear, value: endYear }}
          handleSelectedItemchange={item => {
            item.selectedItem.onClick();
          }}
        />
        <Checkbox
          checked={isCurrent}
          name="isCurrent"
          onChange={e => {
            formik.setFieldValue('isCurrent', e.target.checked);
          }}
        >
          I currently work here
        </Checkbox>
      </div>
      {!!errors.incorrectYear && (
        <Text variant="caption" intent="error">
          {errors.incorrectYear}
        </Text>
      )}
      <Checkbox
        name="isDefault"
        checked={isDefault}
        onChange={e => {
          formik.setFieldValue('isDefault', e.target.checked);
        }}
      >
        Set as default credential
      </Checkbox>
      <div className="flex gap-4 items-center">
        <Button
          onClick={stopSubmit ? noop : formik.handleSubmit}
          disabled={stopSubmit}
          loading={false}
        >
          {props.isEdit ? 'Save' : 'Add'}
        </Button>
        {isEdit && (
          <Button
            onClick={() =>
              dispatch(
                deleteCredential(editedCredential.id, {
                  onSuccess: resetForm(),
                  onFailure: () => {},
                }),
              )
            }
            icon={<Trash />}
          />
        )}
        <Button onClick={() => resetForm()} loading={false} variant="outline">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default PostCredentialForm;

export const PostCredentialFormWithModal = withModal(PostCredentialForm);
